import { useEffect, useRef } from "react";

import { useSocketAuthenticated } from "../../SocketContext";
import api from "../../../../services/api";
import DefaultValuesUtils from "../../../../utils/classes/DefaultValuesUtils";

const SocketMessagesList = ({
  dispatch,
  setAreThereNewMessagesScroll,
  shouldDisplayScrollButton,
  scrollToBottom,
  filesExistenceDictionary,
  setFilesExistenceDictionary
}) => {
  //  ***************
  //  ** Variables **
  //  ***************
  const { getSocket } = useSocketAuthenticated();

  const pendingMediaChecksRef = useRef(new Set());
  const filesExistenceRef = useRef(filesExistenceDictionary);



  //  ****************
  //  ** Use Effect **
  //  ****************
  useEffect(() => {
    filesExistenceRef.current = filesExistenceDictionary;
  }, [filesExistenceDictionary]);

  useEffect(() => {
    const socket = getSocket();

    if (socket) {
      const checkMediaIntoDictionary = (mediaUrl) => {
        return Object.keys(filesExistenceRef.current).includes(mediaUrl);
      };

      const checkMediaWithRetry = async (mediaUrl, fullUrl, type) => {
        if (pendingMediaChecksRef.current.has(fullUrl)) return;
        pendingMediaChecksRef.current.add(fullUrl);

        try {
          let success = false;

          for (let attempt = 0; attempt < DefaultValuesUtils.RETRIES.defaultAttempts; attempt++) {
            try {           
              const response = await api.post("/checkExistence", { mediaUrl });
              const result = response.data;

              if (result) {
                success = true;
                break;
              }
            }
            catch (exception) {}

            await new Promise(resolve => setTimeout(resolve, DefaultValuesUtils.RETRIES.defaultDelayMs));
          }

          setFilesExistenceDictionary(previousValue => ({ ...previousValue, [fullUrl]: success }));
          if (success) scrollToBottom(true);
        }
        finally {
          pendingMediaChecksRef.current.delete(fullUrl);
        }
      };

      const handleAppMessage = (data) => {
        const isTenantIdValid = `${api.defaults.headers.tenantId}` === `${data.tenantId}`;
        if (!isTenantIdValid) return;

        const isCurrentTicketId = (data.message?.ticketId ?? data.ticketId).toString() === window.selectedTicketId;
        if (!isCurrentTicketId) return;

        if (data.action === "createAppMessage") {
          dispatch({ type: "ADD_MESSAGE", payload: data.message });

          if (data.message.mediaUrl) {
            const isMediaIntoDictionary = checkMediaIntoDictionary(data.message.mediaUrl);
            if (isMediaIntoDictionary) return;
            
            const mediaUrl = data.message.mediaUrl.split("/").pop();
            checkMediaWithRetry(mediaUrl, data.message.mediaUrl, "add");
          }
          
          if (shouldDisplayScrollButton) {
            setAreThereNewMessagesScroll(true);
          }

          const shouldScrollToButton = data.isImported
            || !shouldDisplayScrollButton
            || (data.message.fromMe && data.message.userId);

          if (shouldScrollToButton) {
            setAreThereNewMessagesScroll(false);
            scrollToBottom(true);
          }
        }
  
        if (data.action === "updateAppMessage") {
          dispatch({ type: "UPDATE_MESSAGE", payload: data.message });

          if (data.message.mediaUrl) {
            const isMediaIntoDictionary = checkMediaIntoDictionary(data.message.mediaUrl);
            if (isMediaIntoDictionary) return;

            const mediaUrl = data.message.mediaUrl.split("/").pop();
            checkMediaWithRetry(mediaUrl, data.message.mediaUrl, "update");
          }
        }
  
        if (data.action === "transcribeAppMessage") {
          dispatch({ type: "UPDATE_MESSAGE", payload: data.message });
        }
      };
  
      socket.on("appMessage", handleAppMessage);
  
      return () => {
        socket.off("appMessage", handleAppMessage);
      };
    }
    
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch, setAreThereNewMessagesScroll, shouldDisplayScrollButton, scrollToBottom]);

  //  ************
  //  ** Return **
  //  ************
  return (<></>);
};

export default SocketMessagesList;